// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — hallInteriorBuild.jsx   ·   THE WALLS YOU BUILT (OW-42)
// ────────────────────────────────────────────────────────────────────
// Draws a composed Hall's interior (`hallInterior.js`): the walls, the
// doorways and the paint on the floor. PierHome decides where they are;
// this only lays the stone.
//
// THE LAWS (the Hall's, unchanged):
//  · ZERO LIGHTS. A risen layout's walls breathe through their emissive.
//  · MERGED BY MATERIAL — one draw per wall finish, however many walls.
//  · WORLD-SPACE UVs, so the stone runs on across tiles without a seam.
//  · ONE useFrame for every wall.
//
// ⭐ THE WALLS STEP ASIDE FOR THE LENS, tile by tile. A wall between the
// camera and you sinks to its marble dado (its cap riding down with it),
// and stands back up once the lens has passed. Nothing fades — the stone
// above the dado is squashed down in the vertex buffer, so a wall that is
// down is a neat half-wall, not a ghost. In the Architect every wall is
// down (`wallsDown`), the way a building game shows its plan.
// ════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { Merger, boxUV } from "./pierTex";
import { shadowTex } from "./pierPaint";
import { ITEMS } from "./mineriumCatalogue";
import {
  S, STONE, GOLD, wallSurface, floorSurface, applySurface, disposeGeos, disposeMats,
} from "./hallBuild";

const DADO = 0.95;               // the marble wainscot — the Hall's own
const TOP = 5.1;                 // a built wall stops short of the cornice
const CAP = 0.14;
const UH = TOP - DADO - CAP;     // the stone that steps aside
const THICK = 0.46;              // a wall is a wall, not a tile-sized block
const DOOR_H = 2.7;
const JAMB = 0.26;
const HALF = S / 2;

// which way a wall tile reaches: its neighbours that are wall too
const DIRS = [["n", 0, -1], ["e", 1, 0], ["s", 0, 1], ["w", -1, 0]];

// ── the finish a built wall wears ────────────────────────────────────
// 1 = the Hall's worn wall; otherwise the finish's own pattern.
function wallLook(fin, worn) {
  if (fin === 1 || !ITEMS[fin]?.finish) {
    const pat = worn?.wallPattern;
    return { pattern: pat || "stone", color: pat ? (pat === "stone" ? STONE : "#ffffff") : (worn?.wallTint || STONE) };
  }
  const f = ITEMS[fin].finish;
  return { pattern: f.pattern || "stone", color: f.pattern === "stone" ? STONE : "#ffffff" };
}

// ════════════════════════════════════════════════════════════════════
// THE GEOMETRY — every tile's pieces, with the tile they belong to
// ════════════════════════════════════════════════════════════════════
// A merged geometry that remembers, per vertex, its tile and its rest
// height, so a tile's stone can be lowered without a rebuild.
function tagged(geos) {
  // geos: [{ geo, tile }] → one geometry + per-vertex tile + rest y
  const list = geos.filter(Boolean);
  if (!list.length) return null;
  let n = 0;
  for (const g of list) n += g.geo.attributes.position.count;
  const tile = new Uint16Array(n);
  let o = 0;
  for (const g of list) { tile.fill(g.tile, o, o + g.geo.attributes.position.count); o += g.geo.attributes.position.count; }
  const m = new Merger();
  for (const g of list) m.add("x", g.geo);
  const geo = m.build().x;
  boxUV(geo, 0.27);
  const y0 = Float32Array.from(geo.attributes.position.array.filter((_, i) => i % 3 === 1));
  geo.attributes.position.setUsage(THREE.DynamicDrawUsage);
  // each tile's vertices are one contiguous run (its pieces were added together)
  const ranges = new Map();
  for (let v = 0; v < n; v++) {
    const r = ranges.get(tile[v]);
    if (r) r[1] = v + 1; else ranges.set(tile[v], [v, v + 1]);
  }
  geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e5);   // frustumCulled is off; raycasts pass
  geo.userData = { tile, y0, ranges };
  return geo;
}

const box = (w, h, d, x, y, z) => {
  const g = new THREE.BoxGeometry(w, h, d);
  g.translate(x, y, z);
  return g;
};

// the pieces of one plain wall tile at (cx, cz): a post, and an arm toward
// each walled neighbour. `h`/`y` are the band; `t` the thickness.
function wallPieces(cx, cz, mask, t, h, y) {
  const out = [box(t, h, t, cx, y + h / 2, cz)];
  const arm = HALF - t / 2 + 0.002;
  for (const [k, dx, dz] of DIRS) {
    if (!mask[k]) continue;
    const ox = dx * (t / 2 + arm / 2), oz = dz * (t / 2 + arm / 2);
    out.push(dx ? box(arm, h, t, cx + ox, y + h / 2, cz + oz) : box(t, h, arm, cx + ox, y + h / 2, cz + oz));
  }
  return out;
}

// a doorway: two jambs at the tile's edges and a lintel over the opening
function doorPieces(cx, cz, runs, t, y0, y1, withLintel) {
  const out = [];
  const along = runs === "ew";                    // the wall runs east–west
  const jx = HALF - JAMB / 2;
  for (const sd of [-1, 1]) {
    const h = y1 - y0;
    out.push(along ? box(JAMB, h, t, cx + sd * jx, y0 + h / 2, cz) : box(t, h, JAMB, cx, y0 + h / 2, cz + sd * jx));
  }
  if (withLintel && y1 > DOOR_H) {
    const lo = Math.max(y0, DOOR_H), h = y1 - lo;
    out.push(along ? box(S - 2 * JAMB + 0.01, h, t, cx, lo + h / 2, cz) : box(t, h, S - 2 * JAMB + 0.01, cx, lo + h / 2, cz));
  }
  return out;
}

function buildInterior(hall) {
  const tiles = [];            // [{ x, z }] — the index is what vertices carry
  const upper = {};            // finish → [{ geo, tile }]
  const caps = [];
  const dado = new Merger();
  const wallish = (x, y) => hall.wallAt.has(`${x},${y}`) || hall.doorAt.has(`${x},${y}`);
  const baseWall = (x, y) => { const c = hall.map[y]?.[x]; return c != null && c !== 0 && c !== 2; };
  for (const w of hall.walls) {
    const i = tiles.push({ x: w.x * S, z: w.y * S }) - 1;
    const cx = w.x * S, cz = w.y * S;
    const mask = {};
    for (const [k, dx, dy] of DIRS) mask[k] = wallish(w.x + dx, w.y + dy) || baseWall(w.x + dx, w.y + dy);
    for (const g of wallPieces(cx, cz, mask, THICK, UH, DADO)) (upper[w.finish] ||= []).push({ geo: g, tile: i });
    for (const g of wallPieces(cx, cz, mask, THICK + 0.1, CAP, DADO + UH)) caps.push({ geo: g, tile: i });
    for (const g of wallPieces(cx, cz, mask, THICK + 0.08, DADO, 0)) dado.add("marble", g);
    for (const g of wallPieces(cx, cz, mask, THICK + 0.12, 0.05, DADO - 0.02)) dado.add("gold", g);
    dado.plane("shadow", S, S, [cx, 0.009, cz], [-Math.PI / 2, 0, 0]);
  }
  for (const d of hall.doors) {
    const i = tiles.push({ x: d.x * S, z: d.y * S }) - 1;
    const cx = d.x * S, cz = d.y * S;
    for (const g of doorPieces(cx, cz, d.runs, THICK, DADO, DADO + UH, true)) (upper[d.finish] ||= []).push({ geo: g, tile: i });
    for (const g of doorPieces(cx, cz, d.runs, THICK + 0.1, DADO + UH, DADO + UH + CAP, true)) caps.push({ geo: g, tile: i });
    for (const g of doorPieces(cx, cz, d.runs, THICK + 0.08, 0, DADO, false)) dado.add("marble", g);
    for (const g of doorPieces(cx, cz, d.runs, THICK + 0.12, DADO - 0.02, DADO + 0.03, false)) dado.add("gold", g);
    // the threshold: a gold strip you walk over, and the soft dark of a doorway
    const along = d.runs === "ew";
    dado.add("gold", along ? box(S - 2 * JAMB, 0.012, THICK, cx, 0.006, cz) : box(THICK, 0.012, S - 2 * JAMB, cx, 0.006, cz));
    dado.plane("shadow", S * 1.1, S * 1.1, [cx, 0.009, cz], [-Math.PI / 2, 0, 0]);
  }
  const upperGeos = {};
  for (const [fin, list] of Object.entries(upper)) upperGeos[fin] = tagged(list);
  const dg = dado.build();
  if (dg.marble) boxUV(dg.marble, 1 / 3);
  return { tiles, upperGeos, capGeo: tagged(caps), dado: dg };
}

// lower (k → 0) or raise (k → 1) the stone of the tiles that moved, in place,
// uploading only their vertices
function settle(geo, k, moved, mode) {
  if (!geo) return;
  const { y0, ranges } = geo.userData;
  const attr = geo.attributes.position;
  const pos = attr.array;
  let lo = Infinity, hi = -1;
  for (const i of moved) {
    const r = ranges.get(i); if (!r) continue;
    const kk = k[i];
    for (let v = r[0]; v < r[1]; v++) pos[v * 3 + 1] = mode === "shift" ? y0[v] - (1 - kk) * UH : DADO + (y0[v] - DADO) * kk;
    if (r[0] < lo) lo = r[0];
    if (r[1] > hi) hi = r[1];
  }
  if (hi < 0) return;
  attr.clearUpdateRanges();
  attr.addUpdateRange(lo * 3, (hi - lo) * 3);
  attr.needsUpdate = true;
}

// ════════════════════════════════════════════════════════════════════
// ⭐ THE WALLS
// ════════════════════════════════════════════════════════════════════
export function InteriorWalls({ hall, finish, glideRef, wallsDown = false, tapRef = null }) {
  const built = useMemo(() => buildInterior(hall), [hall]);
  useEffect(() => () => {
    Object.values(built.upperGeos).forEach((g) => g && g.dispose());
    built.capGeo?.dispose();
    disposeGeos(built.dado);
  }, [built]);

  const mats = useMemo(() => ({
    marble: new THREE.MeshStandardMaterial({ map: floorSurface("marble").map, color: "#ffffff", roughness: 0.38, metalness: 0.1 }),
    gold: new THREE.MeshStandardMaterial({ color: GOLD, emissive: "#6a4a10", emissiveIntensity: 0.7, metalness: 0.9, roughness: 0.3 }),
    trim: new THREE.MeshStandardMaterial({ color: "#e6e0ec", roughness: 0.6 }),
    shadow: new THREE.MeshBasicMaterial({ map: shadowTex(), transparent: true, depthWrite: false, opacity: 0.55, color: "#000000" }),
  }), []);
  useEffect(() => () => disposeMats(mats), [mats]);

  // one stone material per finish in use — rebuilt only when the WALL look
  // changes (a new floor or light is not a reason)
  const wornPattern = finish?.wallPattern || null, wornTint = finish?.wallTint || null;
  const stone = useMemo(() => {
    const out = {};
    for (const fin of Object.keys(built.upperGeos)) {
      const look = wallLook(fin === "1" ? 1 : fin, { wallPattern: wornPattern, wallTint: wornTint });
      const m = new THREE.MeshStandardMaterial({ roughness: 0.9 });
      applySurface(m, wallSurface(look.pattern), look.color, 0.9);
      m.userData.glow0 = m.emissiveIntensity;
      out[fin] = m;
    }
    return out;
  }, [built, wornPattern, wornTint]);
  useEffect(() => () => Object.values(stone).forEach((m) => m.dispose()), [stone]);

  // per-tile height: 1 standing, 0 stepped aside
  const k = useMemo(() => new Float32Array(built.tiles.length).fill(1), [built]);
  const want = useMemo(() => new Uint8Array(built.tiles.length).fill(1), [built]);
  // a rebuilt Hall, or the Architect's walls-down, snaps rather than eases
  const seen = useRef({ built: null, down: null, glowing: false });
  const moved = useRef([]);

  useFrame(({ camera, clock }, dt) => {
    // ⭐ a risen layout's walls breathe — the emissive swells, no light is added
    const s = seen.current;
    const fresh = s.built !== built || s.down !== wallsDown;
    s.built = built; s.down = wallsDown;
    if (hall.glow) {
      s.glowing = true;
      const b = 0.5 + 0.5 * Math.sin(clock.elapsedTime * 0.9);
      for (const m of Object.values(stone)) {
        if (m.emissiveMap) m.emissiveIntensity = 0.5 + 1.3 * b;
        else { m.emissive.set("#ff8fc8"); m.emissiveIntensity = 0.06 + 0.22 * b; }
      }
      mats.gold.emissiveIntensity = 0.7 + 0.8 * b;
    } else if (s.glowing) {
      // a Hall that stops breathing hands its stone and gold back at rest
      s.glowing = false;
      mats.gold.emissiveIntensity = 0.7;
      for (const [fin, m] of Object.entries(stone)) {
        const look = wallLook(fin === "1" ? 1 : fin, { wallPattern: wornPattern, wallTint: wornTint });
        applySurface(m, wallSurface(look.pattern), look.color, 0.9);
      }
    }
    const n = built.tiles.length;
    if (!n) return;
    const cx = camera.position.x, cz = camera.position.z;
    const px = glideRef?.current?.x ?? cx, pz = glideRef?.current?.z ?? cz;
    const dx = px - cx, dz = pz - cz;
    const L2 = Math.max(1e-4, dx * dx + dz * dz), L = Math.sqrt(L2);
    const mv = moved.current; mv.length = 0;
    for (let i = 0; i < n; i++) {
      const t = built.tiles[i];
      let w = 1;
      if (wallsDown) w = 0;
      else {
        const rx = t.x - cx, rz = t.z - cz;
        const along = (rx * dx + rz * dz) / L2;
        if (along > -0.05 && along < 1.1) {
          const lateral = Math.abs(rx * dz - rz * dx) / L;
          if (lateral < 2.4 + 1.8 * along) w = 0;
        }
        // right beside you, on the lens's side: down
        const ddx = t.x - px, ddz = t.z - pz;
        if (ddx * ddx + ddz * ddz < (S * 1.6) ** 2 && along < 1.02) w = 0;
        // and anything the lens itself is standing against (it would fill
        // the screen with one stone) — a maze is walked from inside it
        if (rx * rx + rz * rz < 4.2 * 4.2) w = 0;
      }
      want[i] = w;
    }
    const rate = fresh ? 1 : 1 - Math.pow(0.002, Math.min(dt || 0.016, 0.05));
    for (let i = 0; i < n; i++) {
      const d = want[i] - k[i];
      if (d === 0) continue;
      k[i] = fresh || Math.abs(d) < 0.01 ? want[i] : k[i] + d * rate;
      mv.push(i);
    }
    if (!mv.length) return;
    for (const g of Object.values(built.upperGeos)) settle(g, k, mv, "scale");
    settle(built.capGeo, k, mv, "shift");
  });

  // ⚑ a click ON a wall is that wall's tile (the floor plane behind it would
  // answer the tile beyond): step a hair into the face, round to a tile
  const onClick = tapRef ? (e) => {
    if (e.delta > 3) return;
    e.stopPropagation();
    const n = e.face?.normal || { x: 0, y: 0, z: 0 };
    tapRef.current?.ground?.((e.point.x - n.x * 0.08) / S, (e.point.z - n.z * 0.08) / S);
  } : undefined;
  return (
    <group onClick={onClick}>
      {Object.entries(built.dado).map(([key, g]) => (mats[key] ? <mesh key={key} geometry={g} material={mats[key]} renderOrder={key === "shadow" ? 1 : 0} /> : null))}
      {Object.entries(built.upperGeos).map(([fin, g]) => (g ? <mesh key={fin} geometry={g} material={stone[fin]} frustumCulled={false} /> : null))}
      {built.capGeo && <mesh geometry={built.capGeo} material={mats.trim} frustumCulled={false} />}
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// ⭐ THE PAINT — a finish laid tile by tile over the worn floor, with a
// gold inlay wherever one floor meets another
// ════════════════════════════════════════════════════════════════════
export function FloorPaint({ hall }) {
  const built = useMemo(() => {
    const at = new Map(hall.floors.map((f) => [`${f.x},${f.y}`, f.finish]));
    const byFin = {};
    const inlay = new Merger();
    for (const f of hall.floors) {
      const g = new THREE.PlaneGeometry(S, S);
      g.rotateX(-Math.PI / 2);
      // ⚑ the layers, bottom up: seams 0.012 · runner 0.014 · PAINT 0.017 ·
      // inlay to 0.019 · rugs from 0.021 (Hall3D lifts floor pieces) — paint
      // covers the runner, never a rug
      g.translate(f.x * S, 0.017, f.y * S);
      (byFin[f.finish] ||= new Merger()).add("x", g);
      for (const [, dx, dy] of DIRS) {
        if (at.get(`${f.x + dx},${f.y + dy}`) === f.finish) continue;
        if (hall.wallAt.has(`${f.x + dx},${f.y + dy}`)) continue;
        const ex = f.x * S + dx * HALF, ez = f.y * S + dy * HALF;
        inlay.add("gold", dx ? box(0.05, 0.002, S, ex, 0.018, ez) : box(S, 0.002, 0.05, ex, 0.018, ez));
      }
    }
    const geos = {};
    for (const [fin, m] of Object.entries(byFin)) { geos[fin] = m.build().x; boxUV(geos[fin], 1 / 3); }
    return { geos, inlay: inlay.build() };
  }, [hall]);
  useEffect(() => () => { disposeGeos(built.geos); disposeGeos(built.inlay); }, [built]);
  const mats = useMemo(() => {
    const out = {};
    for (const fin of Object.keys(built.geos)) {
      const m = new THREE.MeshStandardMaterial();
      applySurface(m, floorSurface(ITEMS[fin]?.finish?.pattern), "#ffffff", 0.85);
      out[fin] = m;
    }
    out.gold = new THREE.MeshStandardMaterial({ color: GOLD, emissive: "#6a4a10", emissiveIntensity: 0.7, metalness: 0.9, roughness: 0.3 });
    return out;
  }, [built]);
  useEffect(() => () => disposeMats(mats), [mats]);
  return (
    <group>
      {Object.entries(built.geos).map(([fin, g]) => <mesh key={fin} geometry={g} material={mats[fin]} />)}
      {built.inlay.gold && <mesh geometry={built.inlay.gold} material={mats.gold} />}
    </group>
  );
}
